import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  SupportState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  IRecord_SupportThread,
  IRecord_SupportThread_Core,
  IRecord_SupportThread_Create,
  IRecord_SupportMessage_Core,
  IRecord_SupportThread_WithUser,
  IRecord_SupportMessage_WithUser,
  ISupportTypeWrapper,
  ISupportType,
} from '@kaidam/fc-shared/src/types/schema/support'

import {
  IPubsub_Message_Type_Support_Message,
  IPubsub_Message_Body_Support_Message
} from '@kaidam/fc-shared/src/types/pubsub'

import {
  IElasticSearchResult,
} from '@kaidam/fc-shared/src/types/elasticsearch'

import {
  IElasticSearch_Source_Support,
} from '@kaidam/fc-shared/src/types/support'

import {
  PayloadSearch,
  PayloadUpdateThread,
  PayloadCreateMessage,
} from '../../types/support'

import {
  NetworkThunk,
} from '../utils/network'

import projectSelectors from '../selectors/project'
import snackbarActions from './snackbar'

import {
  getSocketClientFactory,
} from '../../store/utils/websocket'

import {
  supportThreadChannel,
} from '@kaidam/fc-shared/src/pubsub/channels'

const getThreadSocket = getSocketClientFactory<
  IPubsub_Message_Type_Support_Message,
  IPubsub_Message_Body_Support_Message
>()

const prefix = 'support'

/*

  initial state

*/
const initialState: SupportState = {
  threads: [],
  messages: [],
  searchResults: [],
}

/*

  reducers

*/
const setThreads: ReducerHandler<SupportState, IRecord_SupportThread_WithUser[]> = (state, action) => {
  state.threads = action.payload
}

const setMessages: ReducerHandler<SupportState, IRecord_SupportMessage_WithUser[]> = (state, action) => {
  state.messages = action.payload
}

const setSearchResults: ReducerHandler<SupportState, IElasticSearchResult<IElasticSearch_Source_Support>[]> = (state, action) => {
  state.searchResults = action.payload
}

/*

  thunks

*/

const search: ThunkHandler<ISupportTypeWrapper<PayloadSearch>, void> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IElasticSearchResult<IElasticSearch_Source_Support>[]>(`/support/${payload.type}/search`, {
    params: {
      query: payload.query,
    }
  })
  dispatch(actions.setSearchResults(data))
}

// if the user has support:admin - this means adminListAllThreads
// otherwise it means listUserThreads
const listThreads: ThunkHandler<ISupportType, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_SupportThread_WithUser[]>(`/support/${payload.type}/threads`)
  dispatch(actions.setThreads(data))
}

const getThread: ThunkHandler<ISupportTypeWrapper<{id: string}>, IRecord_SupportThread_WithUser> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_SupportThread_WithUser>(`/support/${payload.type}/threads/${payload.id}`)
  return data
}

// only the originating user can create and update threads
// so it's OK to call "listUserThreads" after each
const createThread: ThunkHandler<ISupportTypeWrapper<IRecord_SupportThread_Create & IRecord_SupportMessage_Core>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IRecord_SupportThread_Create & IRecord_SupportMessage_Core, IRecord_SupportThread>(`/support/${payload.type}/threads`, payload)
  await dispatch(listThreads({
    type: payload.type,
  }))
  dispatch(snackbarActions.setSuccess(`support thread created`))
  return true
}

const updateThread: ThunkHandler<ISupportTypeWrapper<PayloadUpdateThread>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IRecord_SupportThread_Core, IRecord_SupportThread>(`/support/threads/${payload.id}`, payload.data)
  await dispatch(listThreads({
    type: payload.type,
  }))
  dispatch(snackbarActions.setSuccess(`support thread created`))
  return true
}

const listMessages: ThunkHandler<ISupportTypeWrapper<{id: string}>, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const messages = await handlers.get<IRecord_SupportMessage_WithUser[]>(`/support/${payload.type}/threads/${payload.id}/messages`)
  dispatch(actions.setMessages(messages))
}

const createMessage: ThunkHandler<ISupportTypeWrapper<PayloadCreateMessage>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IRecord_SupportMessage_Core, IRecord_SupportMessage_WithUser>(`/support/${payload.type}/threads/${payload.thread}/messages`, payload.data)
  dispatch(listMessages({
    type: payload.type,
    id: payload.thread,
  }))
  return true
}

const subscribeThread: ThunkHandler<IRecord_SupportThread, boolean> = (thread) => async (dispatch, getState) => {
  const channel = supportThreadChannel.get(thread.project, thread.id)
  const socket = getThreadSocket()
  await socket.subscribe(channel, async () => {
    dispatch(listMessages({
      type: thread.type,
      id: thread.id
    }))
  })
  return true
}

const unsubscribeThread: ThunkHandler<IRecord_SupportThread, boolean> = (thread) => async (dispatch, getState) => {
  const channel = supportThreadChannel.get(thread.project, thread.id)
  const socket = getThreadSocket()
  socket.unsubscribe(channel)
  return true
}

/*

  collections

*/
const reducers = {
  setThreads,
  setMessages,
  setSearchResults,
}

export const actions = {
  setThreads: CreateAction(prefix, `setThreads`, setThreads),
  setMessages: CreateAction(prefix, `setMessages`, setMessages),
  setSearchResults: CreateAction(prefix, `setSearchResults`, setSearchResults),
  search: NetworkThunk(prefix, 'search', search, {
    snackbar: true,
  }),
  listThreads: NetworkThunk(prefix, 'listThreads', listThreads, {
    snackbar: true,
  }),
  getThread: NetworkThunk(prefix, 'getThread', getThread, {
    snackbar: true,
  }),
  createThread: NetworkThunk(prefix, 'createThread', createThread, {
    snackbar: true,
  }),
  updateThread: NetworkThunk(prefix, 'updateThread', updateThread, {
    snackbar: true,
  }),
  listMessages: NetworkThunk(prefix, 'listMessages', listMessages, {
    snackbar: true,
  }),
  createMessage: NetworkThunk(prefix, 'createMessage', createMessage, {
    snackbar: true,
  }),
  subscribeThread: NetworkThunk(prefix, 'subscribeThread', subscribeThread, {
    snackbar: true,
  }),
  unsubscribeThread: NetworkThunk(prefix, 'unsubscribeThread', unsubscribeThread, {
    snackbar: true,
  }),
}

export const reducer = CreateReducer<SupportState>({
  prefix,
  initialState,
  reducers,
})

export default actions
